import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, Plus, Layers, Building2, Home as HomeIcon, Ruler } from "lucide-react";
import { IMAGES, SERVICES, APPROACH, PRINCIPLES } from "../mock";

const IconMap = { layers: Layers, building: Building2, home: HomeIcon, ruler: Ruler };

export default function Home() {
  return (
    <main>
      {/* HERO */}
      <section className="relative min-h-screen bg-[#1E2318] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Brick and glass architectural exterior" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E2318]/95 via-[#1E2318]/60 to-[#1E2318]/20" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-[140px] pb-24">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-white/70 mb-10">
            <span className="inline-block w-1.5 h-1.5 bg-[#B29774]" />
            <span>MOHAN LTD</span>
            <span className="w-10 h-px bg-white/30" />
            <span>BUILT WITH PURPOSE</span>
          </div>

          <h1 className="font-display text-[64px] md:text-[96px] lg:text-[112px] leading-[0.95] font-medium tracking-[-0.03em] fade-up">
            Construction<br />
            Built Around<br />
            <span className="text-[#B29774]">Your Project.</span>
          </h1>

          <p className="mt-10 max-w-md text-white/80 leading-relaxed text-[15px]">
            Mohan Ltd provides professional construction and building development services across residential, commercial and specialised projects.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-8">
            <Link to="/contact" className="group inline-flex items-center gap-3 bg-[#B29774] text-white px-8 py-4 hover:bg-[#9d8462] transition-colors">
              Discuss Your Project <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 border-b border-white/60 pb-1 hover:border-[#B29774] hover:text-[#B29774] transition-colors">
              View Our Services <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        {/* Vertical caption */}
        <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 rotate-90 origin-right text-[11px] tracking-[0.3em] text-white/60">
          ARCHITECTURAL STUDY — BRICK & GLASS
        </div>

        {/* Bottom bar */}
        <div className="relative border-t border-white/15">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] tracking-[0.2em] text-white/80">
            <div className="flex items-center gap-3">
              <span className="inline-block w-1.5 h-1.5 bg-[#B29774]" />
              <span>UNITED KINGDOM</span>
            </div>
            <div>PRACTICAL THINKING. PROFESSIONAL CONSTRUCTION.</div>
            <a href="#introduction" className="inline-flex items-center gap-2 hover:text-[#B29774]">
              EXPLORE <ArrowDown size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE STRIP */}
      <section id="introduction" className="bg-[#F9F8F6] border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 divide-y md:divide-y-0 md:divide-x divide-black/10">
            {SERVICES.map((s) => (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                className="group flex items-center gap-4 py-4 md:py-2 md:px-6 first:md:pl-0 last:md:pr-0"
              >
                <span className="text-xs text-[#B29774] tracking-widest">{s.num}</span>
                <span className="text-[15px] font-medium group-hover:text-[#B29774] transition-colors">{s.title}</span>
                <ArrowUpRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="py-24 lg:py-32 bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={IMAGES.intro} alt="Contemporary concrete residential architecture" className="w-full h-[560px] object-cover" />
            <div className="absolute bottom-4 left-4 right-4 flex justify-between text-[10px] tracking-[0.25em] text-[#1A1D14]/70">
              <span>FORM. FUNCTION. DETAIL.</span>
              <span>MOHAN LTD</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-[#1A1D14]/60 mb-6">
              <span>01</span>
              <span className="w-10 h-px bg-[#1A1D14]/30" />
              <span>ABOUT MOHAN LTD</span>
            </div>
            <h2 className="font-display text-[42px] md:text-[56px] leading-[1.05] font-medium tracking-[-0.02em]">
              A Practical Approach<br />to Professional<br /><span className="text-[#1A1D14]/40">Construction.</span>
            </h2>
            <p className="mt-8 text-[#1A1D14]/70 leading-relaxed max-w-xl">
              Mohan Ltd is a UK construction and building development company providing services across residential, commercial and specialised construction projects. Our approach is centred on understanding project requirements, maintaining clear communication and carrying out work with care and attention to detail.
            </p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-2 border-b border-[#1A1D14]/40 pb-1 hover:border-[#B29774] hover:text-[#B29774] transition-colors">
              About Mohan Ltd <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="bg-[#24291E] text-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-white/60 mb-8">
            <span>02</span>
            <span className="w-10 h-px bg-white/30" />
            <span>OUR SERVICES</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-[42px] md:text-[64px] leading-[1.05] font-medium tracking-[-0.02em]">
              Construction &<br /><span className="text-white/50">Building Development.</span>
            </h2>
            <Link to="/services" className="inline-flex items-center gap-2 border-b border-white/40 pb-1 w-fit hover:border-[#B29774] hover:text-[#B29774] transition-colors">
              Explore Our Services <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {SERVICES.map((s) => {
              const Icon = IconMap[s.icon];
              return (
                <Link
                  key={s.id}
                  to={`/services#${s.id}`}
                  className="group bg-[#24291E] p-8 min-h-[320px] flex flex-col justify-between hover:bg-[#2D3324] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={40} strokeWidth={1.25} className="text-white/90" />
                    <span className="text-xs text-white/50 tracking-widest">{s.num}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-[22px] leading-tight font-medium">{s.title}</h3>
                    <p className="mt-3 text-white/60 text-sm leading-relaxed">{s.short}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[13px] text-[#B29774]">
                      Discover service <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 lg:py-32 bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-[#1A1D14]/60 mb-8">
            <span>03</span>
            <span className="w-10 h-px bg-[#1A1D14]/30" />
            <span>THE WAY WE WORK</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-[42px] md:text-[64px] leading-[1.05] font-medium tracking-[-0.02em]">Our Approach.</h2>
            <p className="text-[#1A1D14]/70 text-right max-w-xs">Clear communication. Practical planning.<br />Professional execution.</p>
          </div>

          <div className="mt-12 border-t border-[#1A1D14]/15">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {APPROACH.map((a) => (
                <div key={a.num} className="group border-b lg:border-b-0 lg:border-r border-[#1A1D14]/15 last:border-r-0 p-8 hover:bg-[#EFECE4] transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-[#1A1D14]/50 text-sm">{a.num}</span>
                    <Plus size={16} className="text-[#1A1D14]/50 transition-transform group-hover:rotate-90" />
                  </div>
                  <h3 className="mt-16 font-display text-[24px] font-medium">{a.title}</h3>
                  <p className="mt-4 text-[#1A1D14]/70 text-sm leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#EFECE4] py-6 overflow-hidden border-y border-[#1A1D14]/10">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex items-center text-[#1A1D14] text-[22px] md:text-[28px] font-display tracking-[-0.01em] mr-10">
              <span>PRACTICAL THINKING</span>
              <span className="mx-6 text-[#B29774]">+</span>
              <span>PROFESSIONAL CONSTRUCTION</span>
              <span className="mx-6 text-[#B29774]">+</span>
              <span>PURPOSE IN EVERY DETAIL</span>
              <span className="mx-6 text-[#B29774]">+</span>
            </div>
          ))}
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-24 lg:py-32 bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-[#1A1D14]/60 mb-6">
              <span>04</span>
              <span className="w-10 h-px bg-[#1A1D14]/30" />
              <span>LEADERSHIP</span>
            </div>
            <h2 className="font-display text-[48px] md:text-[64px] leading-[1.05] font-medium tracking-[-0.02em]">Mohanbaabu Raja</h2>
            <div className="mt-3 text-[#B29774]">Chief Executive Officer</div>
            <p className="mt-8 text-[#1A1D14]/70 leading-relaxed max-w-lg">
              Mohanbaabu Raja leads Mohan Ltd with a focus on professional construction delivery, practical project management and maintaining a strong focus on quality throughout the company’s work.
            </p>
            <div className="mt-10 pt-6 border-t border-[#1A1D14]/15 max-w-md">
              <div className="text-[11px] tracking-[0.25em]">MOHAN LTD</div>
              <div className="text-[11px] tracking-[0.25em] text-[#1A1D14]/60 mt-1">CONSTRUCTION & BUILDING DEVELOPMENT</div>
            </div>
          </div>
          <div className="relative">
            <img src={IMAGES.leadership} alt="Sculptural concrete architectural detail" className="w-full h-[600px] object-cover" />
            <div className="absolute left-4 bottom-4 bg-[#24291E]/85 text-white text-[10px] tracking-[0.25em] px-3 py-2">
              STRENGTH IN THE DETAIL.
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-24 lg:py-32 bg-[#EFECE4]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-[#1A1D14]/60 mb-8">
            <span>05</span>
            <span className="w-10 h-px bg-[#1A1D14]/30" />
            <span>WHY MOHAN LTD</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-[42px] md:text-[64px] leading-[1.05] font-medium tracking-[-0.02em]">
              A Professional Approach<br /><span className="text-[#1A1D14]/40">to Construction.</span>
            </h2>
            <p className="text-[#1A1D14]/70 text-right max-w-xs">The principles that shape<br />the way we work.</p>
          </div>

          <div className="mt-12 border-t border-[#1A1D14]/15">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {PRINCIPLES.map((p) => (
                <div key={p.num} className="border-b lg:border-b-0 lg:border-r border-[#1A1D14]/15 last:border-r-0 p-8">
                  <span className="text-[#1A1D14]/50 text-sm">{p.num}</span>
                  <h3 className="mt-14 font-display text-[24px] font-medium">{p.title}</h3>
                  <p className="mt-4 text-[#1A1D14]/70 text-sm leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CtaBanner />
    </main>
  );
}

export function CtaBanner() {
  return (
    <section className="bg-[#A99070] text-white py-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
        <div>
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] text-white/90 mb-6">
            <span className="w-10 h-px bg-white/70" />
            <span>LET’S TALK ABOUT YOUR PROJECT</span>
          </div>
          <h2 className="font-display text-[42px] md:text-[64px] leading-[1.05] font-medium tracking-[-0.02em]">
            A clear starting point.<br />Your next project.
          </h2>
        </div>
        <Link to="/contact" className="inline-flex items-center gap-3 border border-white px-8 py-4 hover:bg-white hover:text-[#1A1D14] transition-colors w-fit">
          Discuss Your Project <ArrowUpRight size={18} />
        </Link>
      </div>
    </section>
  );
}
